'use client'

import { ChatForm } from '../ChatForm/ChatForm'
import { DeleteIcon } from '@repo/ui/DeleteIcon'
import { EditIcon } from '@repo/ui/EditIcon'
import { getRelativeDate } from '@repo/utils/time'
import { HoverActionsPill } from '@repo/ui/HoverActionsPill'
import { useAuthenticationStore } from '@repo/store/useAuthenticationStore'
import { useState } from 'react'
import MUIBox from '@mui/material/Box'
import MUIIconButton from '@mui/material/IconButton'
import MUIStack from '@mui/material/Stack'
import MUITooltip from '@mui/material/Tooltip'
import MUITypography from '@mui/material/Typography'

interface ChatMessageProps {
  message: string
  senderUsername: string
  timestamp: string
  onEdit?: (newMessage: string) => void
  onDelete?: () => void
}

export const ChatMessage = ({ message, senderUsername, timestamp, onEdit, onDelete }: ChatMessageProps) => {
  const { userId } = useAuthenticationStore()
  const isOwnMessage = userId === senderUsername
  const [isEditing, setIsEditing] = useState(false)

  const handleEditClick = () => {
    setIsEditing(true)
  }

  const handleCancelEdit = () => {
    setIsEditing(false)
  }

  const handleSubmitEdit = (editedMessage: string) => {
    if (onEdit) {
      onEdit(editedMessage)
      setIsEditing(false)
    }
  }

  if (isEditing) {
    return (
      <MUIStack data-testid="ChatMessage" direction="row" spacing={2} sx={{ width: '100%' }}>
        <MUIBox sx={{ flex: 1 }}>
          <MUIBox
            sx={{
              backgroundColor: '#DFD1B655',
              borderRadius: '6px',
              padding: '12px 16px'
            }}
          >
            <ChatForm
              onSend={handleSubmitEdit}
              isEditMode={true}
              initialMessage={message}
              onCancel={handleCancelEdit}
            />
          </MUIBox>
        </MUIBox>
      </MUIStack>
    )
  }

  return (
    <MUIStack
      data-testid="ChatMessage"
      direction="row"
      spacing={2}
      sx={{
        width: '100%',
        position: 'relative',
        '&:hover': { '.hoverActionsPill': { visibility: 'visible', opacity: 1 } }
      }}
    >
      <MUIBox sx={{ flex: 1 }}>
        <MUIBox
          sx={{
            backgroundColor: '#F5F6F8',
            borderRadius: '6px',
            padding: '12px 16px',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
          }}
        >
          <MUITypography variant="body1">{message}</MUITypography>
          <MUIStack direction="row" spacing={1} alignItems="baseline" justifyContent={'flex-end'} sx={{ mb: 0.5 }}>
            <MUITypography variant="body2" sx={{ fontWeight: 600 }}>
              {senderUsername}
            </MUITypography>
            <MUITypography variant="caption" sx={{ color: 'text.secondary' }}>
              {getRelativeDate(timestamp)}
            </MUITypography>
          </MUIStack>
          {isOwnMessage && (
            <HoverActionsPill>
              <MUITooltip title="Edit message" arrow placement="top">
                <MUIIconButton onClick={handleEditClick} sx={{ width: '36px', height: '36px' }}>
                  <EditIcon sx={{ width: '18px', height: '18px' }} />
                </MUIIconButton>
              </MUITooltip>
              <MUITooltip title="Delete message" arrow placement="top">
                <MUIIconButton onClick={onDelete} sx={{ width: '36px', height: '36px' }}>
                  <DeleteIcon sx={{ width: '18px', height: '18px' }} />
                </MUIIconButton>
              </MUITooltip>
            </HoverActionsPill>
          )}
        </MUIBox>
      </MUIBox>
    </MUIStack>
  )
}
